import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import API from "api";

const AUTH_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const decodeJwtPayload = (token) => {
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    const normalizedPayload = payload
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(payload.length / 4) * 4, "=");
    return JSON.parse(window.atob(normalizedPayload));
  } catch (_) {
    return null;
  }
};

const buildUserFromToken = (payload) => {
  if (payload?.isSuperAdmin || payload?.role === "superAdmin") {
    return {
      id: payload.id,
      mongoId: payload.mongoId || null,
      role: "superAdmin",
      isSuperAdmin: true,
      name: "Super Admin",
      access: { segments: "all", locations: "all" },
      profileImage: null,
    };
  }

  return null;
};

const getStoredAuth = () => {
  const token = localStorage.getItem("authToken") || localStorage.getItem("token");
  const storedUser = localStorage.getItem("authUser");
  const storedExpiresAt = Number(localStorage.getItem("authExpiresAt") || 0);
  const effectiveStoredExpiresAt =
    storedUser && !storedExpiresAt ? Date.now() + AUTH_TTL_MS : storedExpiresAt;

  if (storedUser && !storedExpiresAt) {
    localStorage.setItem("authExpiresAt", String(effectiveStoredExpiresAt));
  }

  if (!token && (!storedUser || effectiveStoredExpiresAt <= Date.now())) {
    localStorage.removeItem("authUser");
    localStorage.removeItem("authExpiresAt");
    return { token: null, user: null };
  }

  const payload = token ? decodeJwtPayload(token) : null;
  if (token && (!payload?.exp || payload.exp * 1000 <= Date.now())) {
    localStorage.removeItem("authToken");
    if (!storedUser || effectiveStoredExpiresAt <= Date.now()) {
      localStorage.removeItem("authUser");
      localStorage.removeItem("authExpiresAt");
      return { token: null, user: null };
    }
  }

  if (!storedUser) {
    return { token, user: buildUserFromToken(payload) };
  }

  try {
    return { token, user: JSON.parse(storedUser) || buildUserFromToken(payload) };
  } catch (_) {
    localStorage.removeItem("authUser");
    return { token, user: buildUserFromToken(payload) };
  }
};

const roleToEndpoint = {
  superAdmin: "/admin/login",
  subAdmin: "/subAdmin/login",
  editor: "/editor/login",
  districtManager: "/subAdmin/login",
};

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async ({ id, password, role }, { rejectWithValue }) => {
    try {
      const endpoint = roleToEndpoint[role] || roleToEndpoint.superAdmin;
      const { data } = await API.post(endpoint, { id, password });
      const user = data?.user || data?.editor || null;

      if (!user) {
        throw new Error("Login response missing user data.");
      }

      if (data?.token) {
        localStorage.setItem("authToken", data.token);
      }
      localStorage.setItem("authUser", JSON.stringify(user));
      localStorage.setItem("authExpiresAt", String(Date.now() + AUTH_TTL_MS));

      return user;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Login failed. Please try again."
      );
    }
  }
);

export const fetchCurrentUser = createAsyncThunk(
  "auth/fetchCurrentUser",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await API.get("/auth/me");
      if (!data?.user) {
        throw new Error("No active session found.");
      }
      return data.user;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Authentication required.");
    }
  }
);

export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      await API.post("/admin/logout");
      localStorage.removeItem("authToken");
      localStorage.removeItem("authUser");
      localStorage.removeItem("authExpiresAt");
      return true;
    } catch (error) {
      localStorage.removeItem("authToken");
      localStorage.removeItem("authUser");
      localStorage.removeItem("authExpiresAt");
      return rejectWithValue("Logout failed.");
    }
  }
);

const storedAuth = getStoredAuth();

const authSlice = createSlice({
  name: "auth",
  initialState: {
    loading: false,
    checking: false,
    checked: Boolean(storedAuth.user),
    error: null,
    user: storedAuth.user,
  },
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
      state.checked = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.checked = true;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCurrentUser.pending, (state) => {
        state.checking = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.checking = false;
        state.checked = true;
        state.user = action.payload;
        localStorage.setItem("authUser", JSON.stringify(action.payload));
        localStorage.setItem("authExpiresAt", String(Date.now() + AUTH_TTL_MS));
      })
      .addCase(fetchCurrentUser.rejected, (state) => {
        const fallbackAuth = getStoredAuth();
        state.checking = false;
        state.checked = true;
        state.user = fallbackAuth.user;
        state.error = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.loading = false;
        state.error = null;
        state.checked = true;
      })
      .addCase(logout.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { setUser } = authSlice.actions;
export default authSlice.reducer;
